import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Confirm from '../../components/confirm';
import Screen from '../../components/screen';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  checkUserAuthenticated, clearServerCache, goToRoutePage, setWindowTitle
} from '../../utils/helper';
import {
  AdminBackgroundImage, AdminSmallBackgroundImage, ClearCacheIcon, ManageApiKeyIcon,
  ManagerUserIcon, SettingIcon
} from '../../utils/icons';
import { Box, Breadcrumbs, Grid, Link, Paper, Typography } from '../../utils/material-ui-core';

const ManageUsers = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { writeLog, userFunctionAccess, isUserAuthenticated, showAlert } = useContext(AppContext);

  const [clearAllConfirmDialogOpen, setClearAllConfirmDialogOpen] = useState<boolean>(false);

  const showClearAllConfirmDialog = async () => {
    setClearAllConfirmDialogOpen(true);
  };

  const clearAllConfirmDialogResult = async (confirm: boolean) => {
    setClearAllConfirmDialogOpen(false);
    if (confirm) {
      await clearAll();
    }
  };

  const clearAll = async () => {
    try {
      showAlert({ message: "Please wait until the server cache is clear.", severity: "info" });
      await clearServerCache();
      showAlert({ message: "Cache successfully reset.", severity: "success" });
      // eslint-disable-next-line no-empty
    } catch (e) { }
  };

  useEffect(() => {
    writeLog("Admin", "", "", "");
    setWindowTitle("Admin");
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);

      if (!userFunctionAccess.userPermissionAdmin) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }
    };
    initializePage();
  }, [isUserAuthenticated, userFunctionAccess.userPermissionAdmin, showAlert]);

  return (
    <React.Fragment>
      <Screen>
        <Confirm title="Confirmation" content={`<p>Do you want to clear the server cache?</p>`} yesText="CLEAR CACHE" noText="CANCEL" open={clearAllConfirmDialogOpen} callbackOnClose={() => setClearAllConfirmDialogOpen} callbackOnConfirm={clearAllConfirmDialogResult} />

        <Box className={pageStyles.root}>
          <Grid container spacing={3}>
            <Grid item xs={9}>
              <Box className={globalStyles.bottomSpacer}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                    Home
                  </Link>
                  <Typography color="textPrimary">Manage Settings</Typography>
                </Breadcrumbs>
              </Box>
            </Grid>
          </Grid>

          <Box className={pageStyles.lockedMessageImageContainer}></Box>

          <Box className={pageStyles.menuActionContainer}>
            <Grid container>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                <Paper
                  elevation={0}
                  className={pageStyles.menuActionContent}
                  onClick={() => {
                    goToRoutePage("managesettings/users");
                  }}
                >
                  <Box p={1}>
                    <Box display="flex" flexDirection="column" justifyItems="center" alignItems="center">
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionImage}>
                        <ManagerUserIcon color="secondary" />
                      </Box>
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionTitle}>
                        Users
                      </Box>
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionNote}>
                        Manage users and grant or remove access to the sensor admin functionality.
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                <Paper
                  elevation={0}
                  className={pageStyles.menuActionContent}
                  onClick={() => {
                    goToRoutePage("managesettings/apikeys");
                  }}
                >
                  <Box p={1}>
                    <Box display="flex" flexDirection="column" justifyItems="center" alignItems="center">
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionImage}>
                        <ManageApiKeyIcon color="secondary" />
                      </Box>
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionTitle}>
                        API Keys
                      </Box>
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionNote}>
                        Manage client company or other vendor api keys and grant or remove access to the api's.
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                <Paper
                  elevation={0}
                  className={pageStyles.menuActionContent}
                  onClick={() => {
                    goToRoutePage("managesettings/site");
                  }}
                >
                  <Box p={1}>
                    <Box display="flex" flexDirection="column" justifyItems="center" alignItems="center">
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionImage}>
                        <SettingIcon color="secondary" />
                      </Box>
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionTitle}>
                        Site
                      </Box>
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionNote}>
                        Manage to lock application and IoT service, setup bulletin to broadcast to operation, client company and vendors.
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                <Paper
                  elevation={0}
                  className={pageStyles.menuActionContent}
                  onClick={async () => {
                    await showClearAllConfirmDialog();
                  }}
                >
                  <Box p={1}>
                    <Box display="flex" flexDirection="column" justifyItems="center" alignItems="center">
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionImage}>
                        <ClearCacheIcon color="secondary" />
                      </Box>
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionTitle}>
                        Clear Server Cache
                      </Box>
                      <Box display="flex" justifyContent="center" className={pageStyles.menuActionNote}>
                        Clear all cache (Database Connections, App Locks, Recharge, Shopify, Fulfilment, Roles, Templates).
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Screen>
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    menuActionContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      margin: 0,
      backgroundColor: theme.palette.type === "dark" ? "transparent" : "transparent",
      padding: 0,
    },
    menuActionTitle: {
      fontSize: 20,
      fontWeight: "bolder",
      textTransform: "uppercase",
      letterSpacing: 2,
      color: theme.palette.type === "dark" ? theme.palette.grey[300] : theme.palette.grey[700],
      marginBottom: theme.spacing(1),
      textAlign: "center",
      height: "50px",
    },
    menuActionNote: {
      height: "95px",
      textAlign: "center",
      overflow: "auto",
    },
    menuActionImage: {
      paddingTop: 10,
      width: 60,
      height: 60,
      borderWidth: 0,
      borderStyle: "solid",
      borderColor: theme.palette.type === "dark" ? theme.palette.secondary.light : theme.palette.secondary.light,
      borderRadius: "4% 25%",
      marginBottom: theme.spacing(2),
      "& svg": {
        color: theme.palette.secondary.dark,
      },
      "& > :hover": {},
    },
    menuActionContent: {
      margin: "50px 20px 20px 20px",
      backgroundColor: theme.palette.type === "dark" ? "transparent" : "transparent",
      "& .MuiCardHeader-title": {
        fontSize: 24,
      },
      cursor: "pointer",
      transition: "all 0.5s ease-in-out 0s",
      borderRadius: "0%",
      [theme.breakpoints.down("xs")]: {
        borderWidth: 2,
        borderStyle: "solid",
        backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
        borderColor: theme.palette.type === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
      },
      "& > :hover": {
        backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
        transform: "scale(1.2)",
        transition: "all 0.8s ease-in-out 0s",
        [theme.breakpoints.down("xs")]: {
          transform: "scale(1)",
        },
        "& $menuActionImage": {
          "& svg": {
            color: theme.palette.primary.dark,
          },
          transition: "all 0.5s ease-in-out 0s",
        },
      },
    },
    "@keyframes dashboardContainerAnimation": {
      from: { width: "200%", opacity: 0 },
      to: { width: "100%", opacity: 0.9 },
    },
    lockedMessageImageContainer: {
      margin: 0,
      spacing: 0,
      paddingTop: 0,
      marginTop: 20,
      marginBottom: 10,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      backgroundImage: `url(${AdminSmallBackgroundImage})`,
      backgroundPosition: "center left",
      backgroundSize: "cover",
      animation: "$dashboardContainerAnimation 1s",
      transition: "all 0.5s ease-in-out 0s",
      minHeight: 300,
      [theme.breakpoints.up("sm")]: {
        minHeight: 550,
        backgroundImage: `url(${AdminBackgroundImage})`,
        backgroundPosition: "center center",
      },
    },
  })
);

export default ManageUsers;
